import React, { useState, useEffect, useMemo } from "react";
import {
  Home,
  Users,
  GraduationCap,
  Building2,
  BookOpen,
  Settings,
  Calendar,
  Bell,
  Palette,
  UserCog,
  CreditCard,
  FileText,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  href?: string;
  subItems?: MenuItem[];
  roles?: string[];
  permissions?: string[];
}

interface SidebarProps {
  onNavigation: (view: string) => void;
  activeView?: string;
}

const Sidebar: React.FC<SidebarProps> = ({
  onNavigation,
  activeView = "dashboard",
}) => {
  const [activeItem, setActiveItem] = useState<string>(activeView);
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set());
  const { hasPermission, hasAnyRole, user } = useAuth();

  // Debug log
  useEffect(() => {
    console.log("Sidebar - Current user:", user);
    console.log(
      "Sidebar - hasAnyRole(['SchoolManager']):",
      hasAnyRole(["SchoolManager"]),
    );
    console.log(
      "Sidebar - hasPermission('users.view'):",
      hasPermission("users.view"),
    );
  }, [user, hasAnyRole, hasPermission]);

  // activeView değiştiğinde activeItem'ı güncelle
  useEffect(() => {
    setActiveItem(activeView);
  }, [activeView]);

  // Toggle menu expand/collapse
  const toggleMenu = (menuId: string) => {
    setExpandedMenus((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(menuId)) {
        newSet.delete(menuId);
      } else {
        newSet.add(menuId);
      }
      return newSet;
    });
  };

  // Filtrelenmiş menu items
  const filteredMenuItems = useMemo(() => {
    const menuItems: MenuItem[] = [
      {
        id: "dashboard",
        label: "Dashboard",
        icon: <Home className="w-5 h-5" />,
        href: "/dashboard",
        roles: ["Admin", "SchoolManager", "SchoolUser", "ApplicationUser"],
      },
      {
        id: "system-management",
        label: "Sistem Yönetimi",
        icon: <Settings className="w-5 h-5" />,
        roles: ["Admin", "ApplicationUser"],
        subItems: [
          {
            id: "schools",
            label: "Okullar",
            icon: <Building2 className="w-4 h-4" />,
            href: "/schools",
            permissions: ["schools.view"],
          },
          {
            id: "users",
            label: "Okul Yöneticileri",
            icon: <Users className="w-4 h-4" />,
            href: "/users",
            permissions: ["users.view"],
          },
          {
            id: "system-admins",
            label: "Sistem Yöneticileri",
            icon: <UserCog className="w-4 h-4" />,
            href: "/system-admins",
            permissions: ["users.view"],
          },
          {
            id: "brands",
            label: "Markalar",
            icon: <Palette className="w-4 h-4" />,
            href: "/brands",
            permissions: ["system.config"],
          },
        ],
      },
      {
        id: "user-management",
        label: "Kullanıcı Yönetimi",
        icon: <Users className="w-5 h-5" />,
        roles: ["SchoolManager", "SchoolUser"],
        subItems: [
          {
            id: "school-users",
            label: "Okul Kullanıcıları",
            icon: <Users className="w-4 h-4" />,
            href: "/school-users",
            permissions: ["users.view"],
          },
        ],
      },
      {
        id: "education",
        label: "Eğitim Yönetimi",
        icon: <GraduationCap className="w-5 h-5" />,
        roles: ["SchoolManager", "SchoolUser"],
        subItems: [
          {
            id: "classes",
            label: "Sınıflar",
            icon: <BookOpen className="w-4 h-4" />,
            href: "/classes",
            permissions: ["students.view"],
          },
          {
            id: "students",
            label: "Öğrenciler",
            icon: <Users className="w-4 h-4" />,
            href: "/students",
            permissions: ["students.view"],
          },
          {
            id: "parents",
            label: "Veliler",
            icon: <Users className="w-4 h-4" />,
            href: "/parents",
            permissions: ["students.view"],
          },
        ],
      },
      {
        id: "payment-management",
        label: "Ödeme Yönetimi",
        icon: <CreditCard className="w-5 h-5" />,
        roles: ["SchoolManager", "SchoolUser"],
        subItems: [
          {
            id: "school-payments",
            label: "Okul Ödemeleri",
            icon: <CreditCard className="w-4 h-4" />,
            href: "/school-payments",
            permissions: ["finance.view"],
          },
          {
            id: "events",
            label: "Etkinlikler",
            icon: <Calendar className="w-4 h-4" />,
            href: "/events",
            permissions: ["events.view"],
          },
          // {
          //   id: "reports",
          //   label: "Raporlar",
          //   icon: <FileText className="w-4 h-4" />,
          //   href: "/reports",
          //   permissions: ["finance.view"],
          // },
        ],
      },
      {
        id: "notifications",
        label: "Bildirimler",
        icon: <Bell className="w-5 h-5" />,
        href: "/notifications",
        roles: ["Admin", "ApplicationUser"],
        permissions: ["notifications.view"],
      },
    ];

    // Menu item'ın görünür olup olmadığını kontrol et
    const isMenuItemVisible = (item: MenuItem): boolean => {
      // Role kontrolü
      if (item.roles && item.roles.length > 0) {
        if (!hasAnyRole(item.roles)) {
          return false;
        }
      }

      // Permission kontrolü
      if (item.permissions && item.permissions.length > 0) {
        const hasRequiredPermission = item.permissions.some((permission) =>
          hasPermission(permission),
        );
        if (!hasRequiredPermission) {
          return false;
        }
      }

      return true;
    };

    // Submenu item'larını filtrele
    const getFilteredSubItems = (subItems: MenuItem[]): MenuItem[] => {
      return subItems.filter((item) => isMenuItemVisible(item));
    };

    return menuItems
      .filter((item) => isMenuItemVisible(item))
      .map((item) => ({
        ...item,
        subItems: item.subItems
          ? getFilteredSubItems(item.subItems)
          : undefined,
      }))
      .filter((item) => !item.subItems || item.subItems.length > 0); // Boş submenu'leri kaldır
  }, [hasPermission, hasAnyRole]);

  // Aktif olan menüyü otomatik aç
  useEffect(() => {
    filteredMenuItems.forEach((item) => {
      if (item.subItems) {
        const hasActiveSubItem = item.subItems.some(
          (subItem) => subItem.id === activeView,
        );
        if (hasActiveSubItem) {
          setExpandedMenus((prev) => {
            const newSet = new Set(prev);
            newSet.add(item.id);
            return newSet;
          });
        }
      }
    });
  }, [activeView, filteredMenuItems]);

  const handleItemClick = (itemId: string) => {
    setActiveItem(itemId);
    // Navigate to the selected page
    onNavigation(itemId);
    console.log("Navigating to:", itemId);
  };

  const renderMenuItem = (item: MenuItem, level: number = 0) => {
    const isActive = activeItem === item.id;
    const hasSubItems = item.subItems && item.subItems.length > 0;
    const isExpanded = expandedMenus.has(item.id);

    // Check if any subitem is active
    const hasActiveSubItem =
      hasSubItems &&
      item.subItems!.some((subItem) => activeItem === subItem.id);

    return (
      <div key={item.id}>
        <div
          className={`
            flex items-center justify-between px-4 py-3 text-sm font-medium rounded-lg transition-colors cursor-pointer
            ${level === 0 ? "mx-2 mb-1" : "ml-6 mr-2 mb-1"}
            ${
              isActive || hasActiveSubItem
                ? hasSubItems
                  ? "bg-purple-50 text-[#9B51E0] border border-purple-200"
                  : "bg-[#9B51E0] text-white border border-[#9B51E0]"
                : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
            }
          `}
          onClick={() => {
            if (hasSubItems) {
              toggleMenu(item.id);
            } else {
              handleItemClick(item.id);
            }
          }}
        >
          <div className="flex items-center space-x-3">
            <span
              className={
                isActive && !hasSubItems
                  ? "text-white"
                  : hasActiveSubItem
                    ? "text-[#9B51E0]"
                    : "text-gray-500"
              }
            >
              {item.icon}
            </span>
            <span>{item.label}</span>
          </div>
          {hasSubItems && (
            <span
              className={hasActiveSubItem ? "text-[#9B51E0]" : "text-gray-500"}
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </span>
          )}
        </div>

        {hasSubItems && isExpanded && (
          <div className="space-y-1 mt-1">
            {item.subItems!.map((subItem) => (
              <div
                key={subItem.id}
                className={`
                  flex items-center px-4 py-3 text-sm font-medium rounded-lg cursor-pointer transition-colors
                  ml-6 mr-2 mb-1
                  ${
                    activeItem === subItem.id
                      ? "bg-[#9B51E0] text-white border border-[#9B51E0]"
                      : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  }
                `}
                onClick={() => handleItemClick(subItem.id)}
              >
                <div className="flex items-center space-x-3">
                  <span
                    className={
                      activeItem === subItem.id ? "text-white" : "text-gray-500"
                    }
                  >
                    {subItem.icon}
                  </span>
                  <span>{subItem.label}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-64 bg-white border-r border-gray-200 h-screen flex justi flex-col">
      {/* Logo/Brand */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex justify-center items-center space-x-4">
          {/* Logo */}
          <div className="flex-shrink-0">
            <img
              src="/logo.png"
              alt="Sparkion Logo"
              className="h-10 w-auto object-contain"
            />
          </div>
        </div>
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
        {filteredMenuItems.map((item) => renderMenuItem(item))}
      </nav>
    </div>
  );
};

export default Sidebar;
